import api from "./api";

/**
 * What a branch varies about a catalogue service: whether it offers it, whether
 * it is bookable online there, and — when it charges something else — its own
 * price. Timing and booking rules belong to the service itself.
 */
const toPayload = (values) => ({
  price_override:
    values.priceOverride === "" || values.priceOverride == null ? null : Number(values.priceOverride),
  is_available_online: Boolean(values.isAvailableOnline),
  is_active: Boolean(values.isActive),
});

/**
 * GET /business/branches/{branchId}/services
 * `filters` accepts { search, isActive, isAvailableOnline } — the booleans are
 * left out entirely when they are "" (no filter), since false is a real value.
 */
export const fetchServices = async (branchId, { page = 1, perPage = 15, filters = {} } = {}) => {
  const params = { page, per_page: perPage };

  if (filters.search) params.search = filters.search;
  if (filters.isActive !== "" && filters.isActive != null) params.is_active = filters.isActive;
  if (filters.isAvailableOnline !== "" && filters.isAvailableOnline != null) {
    params.is_available_online = filters.isAvailableOnline;
  }

  const { data } = await api.get(`/business/branches/${branchId}/services`, { params });
  return { services: data.data.services, meta: data.meta };
};

/** POST /business/branches/{branchId}/services — start offering a catalogue service. */
export const createService = async (branchId, values) => {
  const { data } = await api.post(`/business/branches/${branchId}/services`, {
    ...toPayload(values),
    service_id: values.serviceId,
  });
  return data.data.service;
};

/** PUT /business/branches/{branchId}/services/{serviceId} */
export const updateService = async (branchId, serviceId, values) => {
  const { data } = await api.put(
    `/business/branches/${branchId}/services/${serviceId}`,
    toPayload(values)
  );
  return data.data.service;
};

/** DELETE /business/branches/{branchId}/services/{serviceId} */
export const deleteService = (branchId, serviceId) =>
  api.delete(`/business/branches/${branchId}/services/${serviceId}`);

/** GET /business/branches/{branchId}/services/available — not offered here yet. */
export const fetchAvailableServices = async (branchId) => {
  const { data } = await api.get(`/business/branches/${branchId}/services/available`);
  return { services: data.data.services, meta: data.meta };
};

/* ------------------------------------------------------- the catalogue itself */

/** GET /business/services — the business's own catalogue, paginated. */
export const fetchCatalogue = async ({ page = 1, perPage = 15, filters = {} } = {}) => {
  const params = { page, per_page: perPage };

  if (filters.search) params.search = filters.search;
  if (filters.category) params.category = filters.category;
  if (filters.isActive !== "" && filters.isActive != null) params.is_active = filters.isActive;

  const { data } = await api.get("/business/services", { params });
  return { services: data.data.services, meta: data.meta };
};

export const fetchCatalogueService = async (id) => {
  const { data } = await api.get(`/business/services/${id}`);
  return data.data.service;
};

/** The catalogue form's values in the API's shape. */
const toCataloguePayload = (values) => ({
  name: values.name,
  description: values.description,
  service_category_id: values.categoryId || null,

  duration: Number(values.duration),
  buffer_before: Number(values.bufferBefore || 0),
  buffer_after: Number(values.bufferAfter || 0),

  price: Number(values.price),
  currency_id: values.currencyId || null,
  // Included unless added on top; the rate only travels with on_top.
  tax_mode: values.taxMode || "included",
  tax_rate: values.taxMode === "on_top" ? Number(values.taxRate) : null,

  // Booking mode, advance limit and minimum notice are no longer set per
  // service: every service books instantly, with no window.
  is_available_online: Boolean(values.isAvailableOnline),

  cancellation_window_hours:
    values.cancellationWindowHours === "" ? null : Number(values.cancellationWindowHours),
  cancellation_fee_percent: Number(values.cancellationFeePercent || 0),
  cancellation_policy: values.cancellationPolicy,

  is_active: Boolean(values.isActive),
  sort_order: Number(values.sortOrder || 0),
});

export const createCatalogueService = async (values) => {
  const { data } = await api.post("/business/services", toCataloguePayload(values));
  return data.data.service;
};

export const updateCatalogueService = async (id, values) => {
  const { data } = await api.put(`/business/services/${id}`, toCataloguePayload(values));
  return data.data.service;
};

export const deleteCatalogueService = (id) => api.delete(`/business/services/${id}`);

/**
 * PUT /business/services/{id}/branches — replaces the set of branches offering
 * the service. A blank price means that branch charges the catalogue price.
 */
export const syncServiceBranches = async (id, branches) => {
  const { data } = await api.put(`/business/services/${id}/branches`, {
    branches: branches.map((branch) => ({
      branch_id: branch.branchId,
      price_override:
        branch.priceOverride === "" || branch.priceOverride == null
          ? null
          : Number(branch.priceOverride),
      is_available_online: Boolean(branch.isAvailableOnline),
      is_active: Boolean(branch.isActive),
    })),
  });
  return data.data.service;
};

/* -------------------------------------------------------------- service groups */

export const fetchServiceCategories = async () => {
  const { data } = await api.get("/business/service-categories");
  return data.data.service_categories;
};

const toCategoryPayload = (values) => ({
  name: values.name,
  description: values.description,
  parent_id: values.parentId || null,
  is_visible_online: Boolean(values.isVisibleOnline),
  is_active: Boolean(values.isActive),
  sort_order: Number(values.sortOrder || 0),
});

export const createServiceCategory = async (values) => {
  const { data } = await api.post("/business/service-categories", toCategoryPayload(values));
  return data.data.service_category;
};

export const updateServiceCategory = async (id, values) => {
  const { data } = await api.put(`/business/service-categories/${id}`, toCategoryPayload(values));
  return data.data.service_category;
};

export const deleteServiceCategory = (id) => api.delete(`/business/service-categories/${id}`);
